import {
  Component,
  OnDestroy,
  OnInit,
  ElementRef,
  ViewChild,
  ViewChildren,
  QueryList,
} from "@angular/core";

import { ConnectableObservable, Subject } from "rxjs";
import { Observable } from "rxjs/Observable";
import { BehaviorSubject } from "rxjs/BehaviorSubject";
import "rxjs/add/operator/do";
import "rxjs/add/operator/takeWhile";
import "rxjs/add/operator/pluck";
import "rxjs/add/operator/reduce";
import "rxjs/add/operator/mergeMap";
import "rxjs/add/operator/take";

import { HexonProvider } from "../../providers/hexon/hexon";
import { ISearch, SearchProvider } from "../../providers/search/search";
import { Content, ModalController } from "ionic-angular";
import { SitesPage } from "../../pages/sites/sites";
import { ISelectedAd, ISite } from "../../models/selectedad";
import { EscapeProvider } from "../../providers/escape/escape";
import { LoginProvider } from "../../providers/login/login";
import { IStatus } from "../../providers/hexon/ad-status";

import { IVehicle } from "../../models/vehicle";
import { IAccount } from "../../models/account";
import { IAd } from "../../models/ads";
import { RefreshProvider } from "../../providers/refresh/refresh";
import { DomProvider } from "../../providers/dom/dom";

@Component({
  selector: "af-overview",
  templateUrl: "overview.html",
})
export class OverviewComponent implements OnInit, OnDestroy {
  public vehicles$: ConnectableObservable<IVehicle[]>;
  public sites$: Observable<ISite[]>;
  public accounts$: Observable<IAccount[]>;
  public status: IStatus;
  public infiniteScroll$: Observable<boolean>;
  public spinner$: Observable<boolean>;
  public searchQuery$: Observable<ISearch>;

  public selectedAd$: Observable<ISelectedAd>;
  public deSelectedAd$: Observable<ISelectedAd>;

  private selectedAdSubject$: BehaviorSubject<ISelectedAd>;

  // true: reset search, false: infinite search
  private nextPage$ = new Subject<boolean>();
  private nextPageRefresh$ = new Observable<boolean>();
  private page$: Observable<{ page: number; search: ISearch }>;

  private readonly emptyAd: ISelectedAd = {
    selected: false,
    stockNumber: -1,
    site: { sitecode: "", sitename: "" },
    account: null,
    dimensions: { left: 0, top: 0, width: 0 },
  };

  @ViewChildren("vehiclesItemAd")
  private vehiclesItemAds: QueryList<ElementRef>;
  @ViewChild(Content)
  private content: Content;

  public static trackBy(index: number, item: IVehicle) {
    return item.identificationLicensePlate;
  }

  public static trackByAd(index: number, item: string) {
    return item;
  }

  constructor(
    /* tslint:disable:no-unused-variable */
    private hexon: HexonProvider,
    private searchServ: SearchProvider,
    private modalCtrl: ModalController,
    private escape: EscapeProvider,
    private login: LoginProvider,
    private refresh: RefreshProvider,
    /* tslint:disable:no-unused-variable */
    private dom: DomProvider
  ) {
    this.accounts$ = hexon.getAccounts$();
    this.status = hexon.status;

    // Start off with deselected ad
    this.selectedAdSubject$ = new BehaviorSubject<ISelectedAd>(this.emptyAd);

    // Deselect ad when user dismisses the popup (pressing ESC or clicking somewhere else)
    const dismiss = this.escape
      .start$()
      .filter(
        (element) =>
          element.nodeName !== "ION-ICON" ||
          element.getAttribute("name") === "radio-button-off"
      );

    // Deselect with a new search too
    this.deSelectedAd$ = Observable.merge(dismiss, this.searchServ.search$).map(
      () => this.emptyAd
    );

    // Only emit selectedAd when it is different
    this.selectedAd$ = Observable.merge(
      this.selectedAdSubject$,
      this.deSelectedAd$
    )
      .distinctUntilChanged(
        (a, b) =>
          a.stockNumber === b.stockNumber && a.site.sitecode === b.site.sitecode
      )
      .shareReplay(1);

    // Merge nextpage and refresh observables
    this.nextPageRefresh$ = Observable.merge(
      this.nextPage$,
      refresh.$.map(() => true)
    );

    // Get increasing page number for every page$ event
    this.page$ = Observable.combineLatest(
      this.nextPageRefresh$,
      this.searchServ.search$
    ).scan(
      (acc, [reset, search]) => ({
        page: reset || search.query !== acc.search.query ? 0 : acc.page + 1,
        search,
      }),
      { page: -1, search: { query: "", field: "" } }
    );

    // Get page with vehicles and fill in accounts property
    const vehiclesPage$ = this.page$
      .switchMap(({ page, search }) =>
        hexon
          .getVehicles$(page, search.query, search.field)
          .map((vehicles) => ({ vehicles, page }))
          .do((vehicles) =>
            vehicles.vehicles.forEach((vehicle) => {
              vehicle.accounts = (vehicle.ads || []).reduce(
                (acc, value) => acc.set(value.site_code, value),
                new Map()
              );
            })
          )
          .do((vehicle) => {
            if (vehicle.page === 0) {
              this.content.scrollToTop(200);
            }
          })
      )
      .share();

    // Append page to existing pages
    this.vehicles$ = vehiclesPage$
      .scan(
        (acc, { vehicles, page }) => {
          return page === 0 ? vehicles : acc.concat(vehicles);
        },
        [] as IVehicle[]
      )
      .publish(); // Wait for all subscribers before emitting

    // Get array of sites for all vehicles
    this.sites$ = Observable.combineLatest(this.vehicles$, this.accounts$)
      .map(([vehicles, accounts]) => {
        return Array.from(
          vehicles.reduce((acc, value) => {
            (value.ads || []).forEach((ad) => acc.add(ad.site_code));
            return acc;
          }, new Set<string>())
        )
          .sort()
          .map((sitecode) => {
            const site = accounts.find(
              (account) => account.site_code === sitecode
            );

            return { sitecode, sitename: site ? site.name : sitecode };
          });
      })
      .shareReplay();

    // Disable scroll when no more vehicles available
    this.infiniteScroll$ = vehiclesPage$.map(
      (data) => data.vehicles.length > 0
    );

    // Start spinner after login and stop after receiving vehicles
    this.spinner$ = Observable.merge(
      this.vehicles$,
      this.login.data$,
      this.searchServ.search$
    ).map((data) => {
      return !("length" in data);
    });

    this.searchQuery$ = this.searchServ.search$.map((search) => {
      let field;
      switch (search.field) {
        case "identification.license_plate":
          field = "kenteken";
          break;
        case "general.make.name":
          field = "merk";
          break;
        case "general.model.name":
          field = "model";
          break;
        case "general.type.name":
          field = "type";
          break;
        default:
          field = "zoekveld";
          break;
      }
      return { ...search, field };
    });
  }

  public ngOnInit() {
    this.login.authenticate();
    this.vehicles$.connect(); // Now start emitting

    this.nextPage$.next(true);
  }

  public ngOnDestroy() {
    this.escape.stop();
  }

  public doInfinite(infiniteScroll) {
    this.nextPage$.next(false);

    return this.vehicles$.first().toPromise();
  }

  public clickAd(stockNumber: number, site: ISite, account: IAd, event) {
    if (account) {
      const top = parseInt(event.target.getBoundingClientRect().top, 10);
      const dimensions = this.getTableAdStyle(top);
      this.selectedAdSubject$.next({
        selected: true,
        stockNumber,
        site,
        account,
        dimensions,
      });
    }
  }

  public async selectVehicle(
    event: Event,
    vehicle: IVehicle,
    status: {},
    accounts$: Observable<IAccount[]>
  ) {
    if (this.isMobile()) {
      event.stopPropagation();
      try {
        const accounts = await accounts$.first().toPromise();
        const modal = this.modalCtrl.create(SitesPage, {
          vehicle,
          status,
          accounts,
        });
        modal.present();
      } catch (e) {
        console.error("Er is een fout opgetreden. accounts$ faalde.");
      }
    }
  }

  /* private */
  private isMobile() {
    return this.dom.matchMediaMatches("(max-width: 870px)");
  }

  private getTableAdStyle(top) {
    let dimensions;

    const scrollArea = this.content.getContentDimensions();

    const dimensionsFirst = this.vehiclesItemAds.first.nativeElement.getBoundingClientRect();

    const dimensionsLast = this.vehiclesItemAds.last.nativeElement.getBoundingClientRect();

    dimensions = {
      "width.px": Math.max(
        dimensionsLast.left - dimensionsFirst.left + 40,
        300
      ),
      "left.px": dimensionsFirst.left - 20,
      "top.px": top - 172 - scrollArea.contentTop + scrollArea.scrollTop,
    };

    return dimensions;
  }

  private onScroll = () => {
    this.selectedAdSubject$.next(this.emptyAd);
  };
}



// WEBPACK FOOTER //
// ./src/components/overview/overview.ts